import {ChangeDetectorRef, Component, CUSTOM_ELEMENTS_SCHEMA, computed, Inject, OnDestroy, OnInit, PLATFORM_ID, signal} from "@angular/core";
import {isPlatformBrowser, NgClass} from "@angular/common";
import {Store} from "@ngrx/store";
import {AppService} from "../../core/service/app.service";
import {
    Router,
    NavigationEnd,
    RouterOutlet,
    ActivatedRoute
} from "@angular/router";
import {TranslateService} from "@ngx-translate/core";
import {FooterComponent} from "../footer/footer.component";
import {HeaderComponent} from "../header/header.component";
import {SidebarComponent} from "../sidebar/sidebar.component";
import {ThemeCustomizerComponent} from "../theme-customizer/theme-customizer.component";
import {filter, map, Subscription} from "rxjs";
import {LoaderService} from "src/app/core/service/loader.service";
import {SessionWarningDialogComponent} from "../../shared/session-warning-dialog/session-warning-dialog.component";
import {SessionService} from "../../core/service/session.service";
import {AuthService} from "../../auth/auth.service";
import {PermissionService} from "src/app/core/service/permission.service";
import {CurrencyService} from "src/app/core/service/currency.service";

@Component({
    selector: "app-root",
    templateUrl: "./app-layout.component.html",
    standalone: true,
    imports: [
        NgClass,
        ThemeCustomizerComponent,
        SidebarComponent,
        HeaderComponent,
        RouterOutlet,
        FooterComponent,
        SessionWarningDialogComponent,
    ],
    schemas: [CUSTOM_ELEMENTS_SCHEMA]
})
export class AppLayout implements OnInit, OnDestroy {
    private static readonly LARGE_SCREEN_MIN_WIDTH = 1024;

    store = signal<any>(null);
    showTopButton = signal(false);
    currentModule = signal<string>("");
    loadingValue = signal(false);
    showWarning = signal(false);
    isLargeScreen = signal(true);
    menuLayoutClass = computed(() => {
        const menu = this.store()?.menu || "collapsed-default";
        return this.isLargeScreen() ? menu : "vertical";
    });

    private readonly isBrowser = signal(false);
    private loaderSub!: Subscription;
    private storeSub!: Subscription;
    private readonly sessionWarningSub: Subscription;
    private currencyChangeSub!: Subscription;
    private routerSubs: Subscription[] = [];
    private readonly scrollHandler = signal<() => void>(() => {});
    private readonly resizeHandler = () => this.updateScreenSize();
    private loaderTimer?: ReturnType<typeof setTimeout>;

    constructor(
        public translate: TranslateService,
        public storeData: Store<any>,
        private readonly service: AppService,
        private readonly router: Router,
        private readonly activatedRoute: ActivatedRoute,
        private readonly loaderService: LoaderService,
        private readonly cdRef: ChangeDetectorRef,
        private readonly sessionService: SessionService,
        private readonly authService: AuthService,
        private readonly permissionService: PermissionService,
        private readonly currencyService: CurrencyService,
        @Inject(PLATFORM_ID) private readonly platformId: Object
    ) {
        this.isBrowser.set(isPlatformBrowser(this.platformId));
        this.initStore();
        this.currentModule.set(this.activatedRoute.snapshot?.url[0]?.path || "");
        this.getLoaderValue();
        this.sessionWarningSub = this.sessionService.showWarning.subscribe((show) => {
            this.showWarning.set(show);
        });
    }

    headerClass = signal<string>("");
    contentPaddingClass = signal<string>("px-6 py-4");
    contentWidthClass = signal<string>("max-w-[1400px]");
    contentMarginClass = signal<string>("mb-10");

    ngOnInit() {
        this.initAnimation();
        this.toggleLoader();

        const computePadding = (url: string): string => {
            if (url.startsWith("/users/profile")) return "p-0";
            if (url.startsWith("/explore-vendor-master/vendor-detail")) return "p-0";
            if (url.startsWith("/system-preference-master/add-new-form")) return "p-0";
            if (url.startsWith("/system-preference-master/save-form")) return "p-0";
            if (url.startsWith("/master/user-detail")) return "p-0";
            if (url.startsWith("/vendor-master/vendor-detail")) return "p-0";
            if (url.startsWith("/event-master/event-detail-page/add-bid")) return "p-0";
            if (url.startsWith("/chat")) return "p-0";
            if (url.startsWith("/ai-assistant-master")) return "px-0";
            if (url.startsWith("/admin-center/save-form")) return "px-0";
            if (url.startsWith("/admin-center/contract-management")) return "px-6 py-4";
            if (url.startsWith("/purchase-order-master/purchase-order-summary")) return "p-0";
            return "px-6 py-4";
        };

        const computeWidth = (): string => {
            return "max-w-[1400px]";
        };

        const computeMargin = (url: string): string => {
            if (url.startsWith("/admin-center/save-po-template")) return "!mb-0";
            return "mb-10";
        };

        this.contentPaddingClass.set(computePadding(this.router.url || ""));
        this.contentWidthClass.set(computeWidth());
        this.contentMarginClass.set(computeMargin(this.router.url || ""));

        const navSub = this.router.events.subscribe((event) => {
            if (event instanceof NavigationEnd) {
                const url = event.urlAfterRedirects || event.url;
                this.contentPaddingClass.set(computePadding(url));
                this.contentWidthClass.set(computeWidth());
                this.contentMarginClass.set(computeMargin(url));
            }
        });
        this.routerSubs.push(navSub);

        if (this.isBrowser()) {
            this.updateScreenSize();
            window.addEventListener("resize", this.resizeHandler, { passive: true });
            this.scrollHandler.set(() => {
                this.showTopButton.set(document.body.scrollTop > 50 || document.documentElement.scrollTop > 50);
            });
            window.addEventListener("scroll", this.scrollHandler, { passive: true });
        }

        const skipUrls = [
            "/purchase-master/purchase-request-list",
            "/purchase-master/line-item-count",
            "/event-master/add-edit-event/general-details",
            "/event-master/add-edit-event/item-details",
            "/event-master/add-edit-event/vendor-details-event",
            "/event-master/add-edit-event/add-product-event",
            "/purchase-master/purchase-items-page",
            "/item-master/item-detail",
        ];

        const localStorageSub = this.router.events.pipe(
            filter((event) => event instanceof NavigationEnd),
            filter((event: NavigationEnd) => {
                const urlPath = event.urlAfterRedirects.split("?")[0];
                return !skipUrls.some((skipUrl) => urlPath.startsWith(skipUrl));
            })
        ).subscribe(() => {
            if (this.isBrowser()) {
                localStorage.removeItem("prCheckboxSelection");
                localStorage.removeItem("prLineItem");
                localStorage.removeItem("categoriesBudget");
            }
        });
        this.routerSubs.push(localStorageSub);

        this.currencyChangeSub = this.currencyService.currencyChange$.subscribe(() => {
            this.cdRef.detectChanges();
        });

        this.permissionService.fetchPermissions();
    }

    getLoaderValue(): void {
        this.loaderSub = this.loaderService.loading$.pipe(map((val) => !!val)).subscribe((val) => {
            this.loadingValue.set(val);
        });
    }

    ngOnDestroy() {
        if (this.loaderSub) this.loaderSub.unsubscribe();
        if (this.storeSub) this.storeSub.unsubscribe();
        if (this.sessionWarningSub) this.sessionWarningSub.unsubscribe();
        if (this.currencyChangeSub) this.currencyChangeSub.unsubscribe();
        this.routerSubs.forEach(sub => sub.unsubscribe());
        this.routerSubs = [];
        if (this.loaderTimer) clearTimeout(this.loaderTimer);
        if (this.isBrowser() && this.scrollHandler()) {
            window.removeEventListener("scroll", this.scrollHandler);
        }
        if (this.isBrowser()) {
            window.removeEventListener("resize", this.resizeHandler);
        }
    }

    private updateScreenSize(): void {
        if (!this.isBrowser()) return;
        this.isLargeScreen.set(window.innerWidth >= AppLayout.LARGE_SCREEN_MIN_WIDTH);
    }

    initAnimation() {
        this.service.changeAnimation();
        const animSub = this.router.events.subscribe((event) => {
            if (event instanceof NavigationEnd) {
                this.service.changeAnimation();
            }
        });
        this.routerSubs.push(animSub);

        if (this.isBrowser()) {
            const ele: any = document.querySelector(".animation");
            ele?.addEventListener("animationend", () => {
                this.service.changeAnimation("remove");
            });
        }
    }

    toggleLoader() {
        this.storeData.dispatch({type: "toggleMainLoader", payload: true});
        this.loaderTimer = setTimeout(() => {
            this.storeData.dispatch({type: "toggleMainLoader", payload: false});
        }, 500);
    }

    initStore() {
        this.storeSub = this.storeData.select((d: any) => d.index).subscribe((d: any) => {
            this.store.set(d);
        });
    }

    goToTop() {
        if (this.isBrowser()) {
            document.body.scrollTop = 0;
            document.documentElement.scrollTop = 0;
        }
    }

    continueSession() {
        const refreshToken: any = this.authService.getRefreshToken();
        const payload = {refreshToken};
        this.authService.authRefreshToken(payload).subscribe({
            next: (res: any) => {
                this.authService.setToken(res?.data?.access_token);
                this.authService.setRefreshToken(res?.data?.refresh_token);
                this.sessionService.confirmStayActive();
            },
            error: () => {
                this.logout();
            }
        });
    }

    logout() {
        this.sessionService.logoutUser();
    }
}
